import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Gift, 
  Copy, 
  Share2, 
  Users, 
  Check, 
  Loader2,
  Star,
  Sparkles,
  Twitter,
  Facebook,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Referral() {
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/referral/info`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setReferralInfo(data);
      }
    } catch (error) {
      console.error("Failed to fetch referral info:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralInfo?.referral_link) return;
    
    try {
      await navigator.clipboard.writeText(referralInfo.referral_link);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = async (platform) => {
    if (!referralInfo) return;
    
    const shareText = `Join me on WornWell.io and track your wardrobe's true value! Use my referral link to get started:`;
    const shareUrl = referralInfo.referral_link;
    
    let url;
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({
              title: "Join WornWell.io",
              text: shareText,
              url: shareUrl
            });
            return;
          } catch (error) {
            console.error("Share failed:", error);
          }
        }
        copyToClipboard();
        return;
    }
    
    window.open(url, "_blank", "width=600,height=400");
  };

  const applyReward = async () => {
    setApplying(true);
    try {
      const response = await authFetch(`${API_URL}/api/referral/apply-reward`, {
        method: "POST",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchReferralInfo();
      } else {
        const error = await response.clone().json().catch(() => ({}));
        toast.error(error.detail || "Failed to apply reward");
      }
    } catch (error) {
      console.error("Apply reward error:", error);
      toast.error("Failed to apply reward");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl mx-auto" data-testid="referral-page">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta-500/10 mb-4">
            <Gift className="w-8 h-8 text-terracotta-500" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
            Invite Friends, Get Rewards
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Share WornWell with friends and earn a free month of Full Closet for each friend who signs up!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border border-border shadow-none text-center">
            <CardContent className="p-6">
              <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="font-heading text-3xl font-bold text-primary">
                {referralInfo?.referral_count || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Friends Joined</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none text-center">
            <CardContent className="p-6">
              <Star className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="font-heading text-3xl font-bold text-primary">
                {referralInfo?.pending_rewards || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Rewards Pending</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none text-center">
            <CardContent className="p-6">
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="font-heading text-3xl font-bold text-primary">
                {referralInfo?.total_free_months_earned || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Months Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Reward Banner */}
        {referralInfo?.pending_rewards > 0 && (
          <Card className="border-2 border-amber-400 bg-amber-50 shadow-none">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">
                      You have {referralInfo.pending_rewards} free month{referralInfo.pending_rewards > 1 ? 's' : ''} to claim!
                    </p>
                    <p className="text-sm text-amber-700">
                      Activate your free Full Closet subscription
                    </p>
                  </div>
                </div>
                <Button
                  onClick={applyReward}
                  disabled={applying}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  data-testid="apply-reward-btn"
                >
                  {applying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Claim Reward"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Share Link Card */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-lg">Your Referral Link</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4 space-y-4">
            <div className="flex gap-2">
              <Input
                value={referralInfo?.referral_link || ""}
                readOnly
                className="bg-sand-50 font-mono text-sm"
                data-testid="referral-link-input"
              />
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Your code:</span>
              <Badge variant="secondary" className="font-mono">
                {referralInfo?.referral_code}
              </Badge>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-sm text-muted-foreground mb-3">Share via</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("twitter")}
                  className="flex-1"
                >
                  <Twitter className="w-4 h-4 mr-2" />
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("facebook")}
                  className="flex-1"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("whatsapp")}
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("native")}
                  className="flex-1"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  More
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How it Works */}
        <Card className="border border-border shadow-none bg-sand-50">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-lg">How It Works</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mx-auto mb-3">
                  1
                </div>
                <p className="font-medium mb-1">Share Your Link</p>
                <p className="text-sm text-muted-foreground">
                  Send your unique referral link to friends who love fashion
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mx-auto mb-3">
                  2
                </div>
                <p className="font-medium mb-1">Friend Signs Up</p>
                <p className="text-sm text-muted-foreground">
                  When they create an account using your link, you both win
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center mx-auto mb-3">
                  3
                </div>
                <p className="font-medium mb-1">Get Rewarded</p>
                <p className="text-sm text-muted-foreground">
                  You earn a free month of Full Closet ($6.99 value)!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rewards History */}
        {referralInfo?.rewards?.length > 0 && (
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-lg">Rewards History</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <div className="space-y-3">
                {referralInfo.rewards.map((reward, idx) => (
                  <div 
                    key={reward.reward_id || idx} 
                    className="flex items-center justify-between p-3 bg-sand-50 rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <Gift className="w-4 h-4 text-terracotta-500" />
                      <div>
                        <p className="text-sm font-medium">Free Month of Full Closet</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(reward.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant={reward.status === "applied" ? "default" : "secondary"}
                      className={reward.status === "applied" ? "bg-green-500" : ""}
                    >
                      {reward.status === "applied" ? "Used" : "Available"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
